/**
 * FormConcierge-FileText — turns a dropped file into markdown for the form corpus (contract §7 + C8 + D15).
 *
 * `convertFileToMarkdown(file)` never rejects: anything it cannot read comes back as `{ Markdown: null }`
 * with a note, and the original still uploads. Input parsed is capped at 25 MB (and 200 MB once a zip-based
 * Office file is inflated), output at 400 000 characters.
 *
 * - .md .markdown .txt → the text; .csv .tsv → a markdown table up to 2 000 rows, else fenced text;
 *   .json → fenced json (pretty when valid); .xml .html .htm → fenced text (never rendered).
 * - .xlsx .xlsm .xls .ods → SheetJS: one `## Sheet: <name>` table per visible sheet (≤ 2 000 rows, ≤ 50 cols).
 * - .docx / .pptx → SheetJS's zip reader (XLSX.CFB) + DOMParser, best-effort headings, lists and tables.
 * - .pdf → pdf.js loaded on first use from js/pdfjs-dist; a near-textless PDF "looks scanned" → null.
 *
 * SheetJS is the page global `window.XLSX`: the build browserify-ignores `xlsx`, so `require('xlsx')` is an
 * empty stub there (same guard as data-import/DataImport-Xlsx-Parser.js). XLSX, DOMParser and the pdf.js
 * loader are injectable for tests; defaults are resolved at call time. No window / document access at load.
 */

/** Largest file parsed. */
const INPUT_CAP_BYTES = 25 * 1024 * 1024;
/** Largest total an Office zip may inflate to before it is refused (zip-bomb guard). */
const INFLATED_CAP_BYTES = 200 * 1024 * 1024;
/** Longest markdown returned. */
const OUTPUT_CAP_CHARS = 400000;
/** Rows per table (header included). */
const TABLE_ROW_CAP = 2000;
/** Columns per table. */
const TABLE_COLUMN_CAP = 50;
/** Below this many non-space characters per page a PDF is treated as scanned. */
const SCANNED_CHARS_PER_PAGE = 20;
/** Pages read from one PDF. */
const PDF_PAGE_CAP = 1000;
/** pdf.js module and worker, served beside index.html (app package.json copyFiles). */
const PDFJS_SCRIPT_SRC = 'js/pdfjs-dist/build/pdf.mjs';
const PDFJS_WORKER_SRC = 'js/pdfjs-dist/build/pdf.worker.mjs';
/** How long the pdf.js script may take to load, and one PDF to convert. */
const PDFJS_LOAD_TIMEOUT_MS = 30000;
const PDF_CONVERT_TIMEOUT_MS = 120000;

const NS_WORD = [ 'http://schemas.openxmlformats.org/wordprocessingml/2006/main', 'http://purl.oclc.org/ooxml/wordprocessingml/main' ];
const NS_DRAWING = [ 'http://schemas.openxmlformats.org/drawingml/2006/main', 'http://purl.oclc.org/ooxml/drawingml/main' ];
const NS_PRESENTATION = [ 'http://schemas.openxmlformats.org/presentationml/2006/main', 'http://purl.oclc.org/ooxml/presentationml/main' ];
const NS_MARKUP_COMPATIBILITY = [ 'http://schemas.openxmlformats.org/markup-compatibility/2006' ];

/** @type {Record<string, string>} */
const _KindByExtension =
	{
		md: 'text', markdown: 'text', txt: 'text',
		csv: 'csv', tsv: 'csv',
		json: 'json',
		xml: 'markup', html: 'markup', htm: 'markup',
		xlsx: 'spreadsheet', xlsm: 'spreadsheet', xls: 'spreadsheet', ods: 'spreadsheet',
		docx: 'docx',
		pptx: 'pptx',
		pdf: 'pdf',
	};

/** @type {Record<string, string>} */
const _KindByType =
	{
		'text/markdown': 'text',
		'text/plain': 'text',
		'text/csv': 'csv',
		'text/tab-separated-values': 'csv',
		'application/json': 'json',
		'text/xml': 'markup',
		'application/xml': 'markup',
		'text/html': 'markup',
		'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'spreadsheet',
		'application/vnd.ms-excel.sheet.macroenabled.12': 'spreadsheet',
		'application/vnd.ms-excel': 'spreadsheet',
		'application/vnd.oasis.opendocument.spreadsheet': 'spreadsheet',
		'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
		'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
		'application/pdf': 'pdf',
	};

const _TextNativeExtensions = [ 'md', 'markdown', 'txt', 'csv', 'tsv', 'json' ];
/** Kinds whose bytes are never text, whatever MIME type the browser guessed. */
const _BinaryKinds = [ 'spreadsheet', 'docx', 'pptx', 'pdf', 'binary' ];

/** @type {any} Cached pdf.js load. */
let _PdfJsPromise = null;

/**
 * The global object, typed loosely (pdf.js and SheetJS hang off it).
 *
 * @return {any} globalThis / window / self, or an empty object.
 */
function _global()
{
	if (typeof globalThis !== 'undefined')
	{
		return /** @type {any} */ (globalThis);
	}
	if (typeof window !== 'undefined')
	{
		return /** @type {any} */ (window);
	}
	return {};
}

/**
 * Lower-cased extension of a file name, without the dot.
 *
 * @param {any} pName - the file name.
 *
 * @return {string} 'xlsx', or '' when there is none.
 */
function _extensionOf(pName)
{
	const tmpName = (typeof pName === 'string') ? pName.trim() : '';
	const tmpDot = tmpName.lastIndexOf('.');
	return ((tmpDot >= 0) && (tmpDot < tmpName.length - 1)) ? tmpName.slice(tmpDot + 1).toLowerCase() : '';
}

/**
 * What kind of file this is, by extension first and MIME type second.
 *
 * TextNative (C8 upload rule): text/*, application/json, or a .md/.markdown/.txt/.csv/.tsv/.json name —
 * except that a name saying spreadsheet / Word / PowerPoint / PDF is never text, whatever the MIME type.
 *
 * @param {any} pName - file name.
 * @param {any} pType - MIME type ('' when the browser did not know).
 *
 * @return {{ Kind: string, TextNative: boolean }} the classification.
 */
function classifyFile(pName, pType)
{
	const tmpExtension = _extensionOf(pName);
	const tmpType = ((typeof pType === 'string') ? pType : '').split(';')[0].trim().toLowerCase();
	let tmpKind = _KindByExtension[tmpExtension] || _KindByType[tmpType] || '';
	if (!tmpKind)
	{
		tmpKind = (tmpType.indexOf('text/') === 0) ? 'text' : 'binary';
	}
	const tmpNativeSignal = (tmpType.indexOf('text/') === 0) || (tmpType === 'application/json') || (_TextNativeExtensions.indexOf(tmpExtension) >= 0);
	return { Kind: tmpKind, TextNative: tmpNativeSignal && (_BinaryKinds.indexOf(tmpKind) < 0) };
}

/**
 * A failed conversion.
 *
 * @param {string} pKind - the file kind.
 * @param {string} pNote - why.
 *
 * @return {{ Markdown: null, Kind: string, Notes: Array<string> }} the result.
 */
function _noText(pKind, pNote)
{
	return { Markdown: null, Kind: pKind, Notes: [ pNote ] };
}

/**
 * A short, single-line error message for a note.
 *
 * @param {any} pError - the error.
 *
 * @return {string} the message.
 */
function _errorText(pError)
{
	const tmpMessage = (pError && pError.message) ? String(pError.message) : String(pError);
	return tmpMessage.replace(/[\x00-\x1f\x7f]+/g, ' ').trim().slice(0, 160);
}

/**
 * Resolves SheetJS: the injected one, else the page global, else `require('xlsx')`; null unless it is a real
 * SheetJS (the browserify-ignore stub is `{}`).
 *
 * @param {any} pDeps - injected dependencies.
 *
 * @return {any} SheetJS or null.
 */
function _resolveXLSX(pDeps)
{
	/** @type {any} */
	let tmpXLSX = pDeps ? pDeps.XLSX : null;
	if (!tmpXLSX && (typeof window !== 'undefined'))
	{
		tmpXLSX = /** @type {any} */ (window).XLSX;
	}
	if (!tmpXLSX)
	{
		try
		{
			tmpXLSX = require('xlsx');
		}
		catch (pError)
		{
			tmpXLSX = null;
		}
	}
	return (tmpXLSX && (typeof tmpXLSX.read === 'function')) ? tmpXLSX : null;
}

/**
 * Resolves a DOMParser constructor: the injected one, else the page's.
 *
 * @param {any} pDeps - injected dependencies.
 *
 * @return {any} the constructor or null.
 */
function _resolveDOMParser(pDeps)
{
	if (pDeps && (typeof pDeps.DOMParser === 'function'))
	{
		return pDeps.DOMParser;
	}
	const tmpGlobal = _global();
	return (typeof tmpGlobal.DOMParser === 'function') ? tmpGlobal.DOMParser : null;
}

/**
 * Reads a file's bytes (File.arrayBuffer, else FileReader).
 *
 * @param {any} pFile - a File-like object.
 *
 * @return {Promise<Uint8Array|null>} the bytes, or null when the object cannot give them.
 */
function _readBytes(pFile)
{
	if (typeof pFile.arrayBuffer === 'function')
	{
		return Promise.resolve(pFile.arrayBuffer()).then((pBuffer) => _toBytes(pBuffer));
	}
	const tmpGlobal = _global();
	if ((typeof tmpGlobal.FileReader === 'function') && (typeof tmpGlobal.Blob === 'function') && (pFile instanceof tmpGlobal.Blob))
	{
		return new Promise((fResolve, fReject) =>
		{
			const tmpReader = new tmpGlobal.FileReader();
			tmpReader.onload = () => fResolve(_toBytes(tmpReader.result));
			tmpReader.onerror = () => fReject(tmpReader.error || new Error('The file could not be read'));
			tmpReader.readAsArrayBuffer(pFile);
		});
	}
	return Promise.resolve(null);
}

/**
 * A Uint8Array view of whatever a reader returned.
 *
 * @param {any} pValue - ArrayBuffer, typed array or Buffer.
 *
 * @return {Uint8Array|null} the bytes.
 */
function _toBytes(pValue)
{
	if (!pValue)
	{
		return null;
	}
	if (pValue instanceof Uint8Array)
	{
		return pValue;
	}
	if ((typeof ArrayBuffer !== 'undefined') && ArrayBuffer.isView && ArrayBuffer.isView(pValue))
	{
		return new Uint8Array(pValue.buffer, pValue.byteOffset, pValue.byteLength);
	}
	if ((typeof pValue.byteLength === 'number') && (typeof pValue.slice === 'function'))
	{
		return new Uint8Array(pValue);
	}
	return null;
}

/**
 * Decodes text bytes: UTF-16 when a byte-order mark says so, else UTF-8; a leading BOM is dropped.
 *
 * @param {Uint8Array} pBytes - the bytes.
 *
 * @return {string} the text.
 */
function _decodeText(pBytes)
{
	let tmpEncoding = 'utf-8';
	if ((pBytes.length >= 2) && (pBytes[0] === 0xFF) && (pBytes[1] === 0xFE))
	{
		tmpEncoding = 'utf-16le';
	}
	else if ((pBytes.length >= 2) && (pBytes[0] === 0xFE) && (pBytes[1] === 0xFF))
	{
		tmpEncoding = 'utf-16be';
	}
	let tmpText = '';
	if (typeof TextDecoder === 'function')
	{
		tmpText = new TextDecoder(tmpEncoding).decode(pBytes);
	}
	else
	{
		// Latin-1 is better than nothing on a browser without TextDecoder.
		for (let i = 0; i < pBytes.length; i += 8192)
		{
			tmpText += String.fromCharCode.apply(null, Array.prototype.slice.call(pBytes, i, i + 8192));
		}
	}
	return (tmpText.charCodeAt(0) === 0xFEFF) ? tmpText.slice(1) : tmpText;
}

/**
 * Whether a character code is whitespace for trimming (space, tab, CR, LF, FF, VT, NBSP).
 *
 * @param {number} pCode - a char code.
 *
 * @return {boolean} true for whitespace.
 */
function _isSpaceCode(pCode)
{
	return (pCode === 32) || ((pCode >= 9) && (pCode <= 13)) || (pCode === 160);
}

/**
 * Drops trailing whitespace in linear time (`/\s+$/` is quadratic on long whitespace runs).
 *
 * @param {string} pText - the text.
 *
 * @return {string} the text without trailing whitespace.
 */
function _trimEnd(pText)
{
	let tmpEnd = pText.length;
	while ((tmpEnd > 0) && _isSpaceCode(pText.charCodeAt(tmpEnd - 1)))
	{
		tmpEnd--;
	}
	return pText.slice(0, tmpEnd);
}

/**
 * Drops trailing spaces and tabs from every line (linear; `/[ \t]+\n/` is quadratic).
 *
 * @param {string} pText - the text.
 *
 * @return {string} the text.
 */
function _trimLineEnds(pText)
{
	return pText.split('\n').map((pLine) =>
	{
		let tmpEnd = pLine.length;
		while ((tmpEnd > 0) && ((pLine.charCodeAt(tmpEnd - 1) === 32) || (pLine.charCodeAt(tmpEnd - 1) === 9)))
		{
			tmpEnd--;
		}
		return pLine.slice(0, tmpEnd);
	}).join('\n');
}

/**
 * Joins the lines of a text into one line (headings, slide bullets).
 *
 * @param {string} pText - the text.
 *
 * @return {string} one trimmed line.
 */
function _joinLines(pText)
{
	return pText.split('\n').map((pLine) => pLine.trim()).filter((pLine) => pLine).join(' ');
}

/**
 * Cuts markdown to the output cap, noting it.
 *
 * @param {string} pMarkdown - the markdown.
 * @param {Array<string>} pNotes - notes to add to.
 *
 * @return {string} the capped markdown.
 */
function _capOutput(pMarkdown, pNotes)
{
	if (pMarkdown.length <= OUTPUT_CAP_CHARS)
	{
		return pMarkdown;
	}
	const tmpSuffix = '\n\n… (truncated)\n';
	pNotes.push('Truncated to 400,000 characters');
	return pMarkdown.slice(0, OUTPUT_CAP_CHARS - tmpSuffix.length) + tmpSuffix;
}

/**
 * A fenced code block whose fence is longer than any backtick run in the text, capped to fit the output.
 *
 * @param {string} pText - block content.
 * @param {string} pLanguage - info string ('' for none).
 * @param {Array<string>} pNotes - notes to add to.
 *
 * @return {string} the fenced block.
 */
function _fence(pText, pLanguage, pNotes)
{
	let tmpLongest = 0;
	const tmpRuns = pText.match(/`+/g) || [];
	tmpRuns.forEach((pRun) =>
	{
		tmpLongest = Math.max(tmpLongest, pRun.length);
	});
	const tmpFence = new Array(Math.max(3, tmpLongest + 1) + 1).join('`');
	const tmpBudget = OUTPUT_CAP_CHARS - (2 * tmpFence.length) - pLanguage.length - 32;
	let tmpText = _trimEnd(pText);
	if (tmpText.length > tmpBudget)
	{
		tmpText = `${ tmpText.slice(0, tmpBudget) }\n… (truncated)`;
		pNotes.push('Truncated to 400,000 characters');
	}
	return `${ tmpFence }${ pLanguage }\n${ tmpText }\n${ tmpFence }\n`;
}

/**
 * One markdown table cell: pipes escaped (and any backslashes right before them), line breaks as <br>,
 * control characters dropped.
 *
 * @param {any} pValue - the cell value.
 *
 * @return {string} cell text.
 */
function _tableCell(pValue)
{
	const tmpText = ((pValue === null) || (typeof pValue === 'undefined')) ? '' : String(pValue);
	// One linear pass: a backslash run right before a pipe is doubled so the pipe stays escaped.
	let tmpOut = '';
	let tmpSlashes = 0;
	for (let i = 0; i < tmpText.length; i++)
	{
		const tmpChar = tmpText.charAt(i);
		const tmpCode = tmpText.charCodeAt(i);
		if (((tmpCode < 32) && (tmpCode !== 9) && (tmpCode !== 10) && (tmpCode !== 13)) || (tmpCode === 127))
		{
			// Control characters are dropped (they come from binary junk); a backslash run carries on across them.
			continue;
		}
		if (tmpChar === '\\')
		{
			tmpSlashes++;
			tmpOut += tmpChar;
			continue;
		}
		if (tmpChar === '|')
		{
			tmpOut += `${ new Array(tmpSlashes + 1).join('\\') }\\|`;
		}
		else if ((tmpChar === '\r') || (tmpChar === '\n'))
		{
			if (!((tmpChar === '\n') && (tmpText.charAt(i - 1) === '\r')))
			{
				tmpOut += '<br>';
			}
		}
		else
		{
			tmpOut += tmpChar;
		}
		tmpSlashes = 0;
	}
	return tmpOut.trim();
}

/**
 * Whether any cell of any row has text once cleaned.
 *
 * @param {Array<Array<any>>} pRows - rows.
 *
 * @return {boolean} true when there is something to show.
 */
function _rowsHaveText(pRows)
{
	return pRows.some((pRow) => pRow.some((pCell) => _tableCell(pCell) !== ''));
}

/**
 * A GFM table from rows of cells, first row as the header, columns capped.
 *
 * @param {Array<Array<any>>} pRows - rows (at least one).
 * @param {Array<string>} pNotes - notes to add to.
 * @param {string} pWhere - 'sheet 2' / 'table 1' for notes.
 *
 * @return {string} the table markdown.
 */
function _markdownTable(pRows, pNotes, pWhere)
{
	let tmpColumns = 0;
	pRows.forEach((pRow) =>
	{
		tmpColumns = Math.max(tmpColumns, pRow.length);
	});
	if (tmpColumns > TABLE_COLUMN_CAP)
	{
		pNotes.push(`${ pWhere }: first ${ TABLE_COLUMN_CAP } of ${ tmpColumns } columns`);
		tmpColumns = TABLE_COLUMN_CAP;
	}
	tmpColumns = Math.max(tmpColumns, 1);
	/**
	 * One table line.
	 * @param {Array<any>} pRow - cells.
	 * @return {string} the line.
	 */
	const fLine = (pRow) =>
	{
		const tmpCells = [];
		for (let i = 0; i < tmpColumns; i++)
		{
			tmpCells.push(_tableCell(pRow[i]));
		}
		return `| ${ tmpCells.join(' | ') } |`;
	};
	const tmpSeparator = [];
	for (let i = 0; i < tmpColumns; i++)
	{
		tmpSeparator.push('---');
	}
	const tmpLines = [ fLine(pRows[0]), `| ${ tmpSeparator.join(' | ') } |` ];
	for (let i = 1; i < pRows.length; i++)
	{
		tmpLines.push(fLine(pRows[i]));
	}
	return `${ tmpLines.join('\n') }\n`;
}

/**
 * Parses delimited text (quoted fields with "" escapes, CR/LF/CRLF rows), stopping after pMaxRows.
 *
 * @param {string} pText - the text.
 * @param {string} pDelimiter - ',' or '\t'.
 * @param {number} pMaxRows - rows to collect.
 *
 * @return {{ Rows: Array<Array<string>>, More: boolean }} rows, and whether more remained.
 */
function _parseDelimited(pText, pDelimiter, pMaxRows)
{
	const tmpRows = [];
	let tmpRow = [];
	let tmpField = '';
	let tmpQuoted = false;
	let tmpFieldStart = true;
	let i = 0;
	/** Ends the current row (blank lines are skipped). */
	const fEndRow = () =>
	{
		tmpRow.push(tmpField);
		if ((tmpRow.length > 1) || (tmpRow[0] !== ''))
		{
			tmpRows.push(tmpRow);
		}
		tmpRow = [];
		tmpField = '';
		tmpFieldStart = true;
	};
	while (i < pText.length)
	{
		const tmpChar = pText.charAt(i);
		if (tmpQuoted)
		{
			if (tmpChar === '"')
			{
				if (pText.charAt(i + 1) === '"')
				{
					tmpField += '"';
					i += 2;
					continue;
				}
				tmpQuoted = false;
			}
			else
			{
				tmpField += tmpChar;
			}
			i++;
			continue;
		}
		if ((tmpChar === '"') && tmpFieldStart)
		{
			tmpQuoted = true;
			tmpFieldStart = false;
		}
		else if (tmpChar === pDelimiter)
		{
			tmpRow.push(tmpField);
			tmpField = '';
			tmpFieldStart = true;
		}
		else if ((tmpChar === '\n') || (tmpChar === '\r'))
		{
			fEndRow();
			if ((tmpChar === '\r') && (pText.charAt(i + 1) === '\n'))
			{
				i++;
			}
			if (tmpRows.length > pMaxRows)
			{
				return { Rows: tmpRows.slice(0, pMaxRows), More: true };
			}
		}
		else
		{
			tmpField += tmpChar;
			tmpFieldStart = false;
		}
		i++;
	}
	if ((tmpField !== '') || (tmpRow.length > 0))
	{
		fEndRow();
	}
	return { Rows: tmpRows.slice(0, pMaxRows), More: tmpRows.length > pMaxRows };
}

/**
 * The delimiter of a .csv: an Excel `sep=X` first line wins, else whichever of , ; or tab the first line
 * uses most outside quotes (European Excel exports use ;).
 *
 * @param {string} pText - the text.
 *
 * @return {{ Delimiter: string, Text: string }} the delimiter, and the text without a sep= line.
 */
function _sniffDelimiter(pText)
{
	const tmpSep = /^sep=(.)\r?\n/i.exec(pText.slice(0, 16));
	if (tmpSep)
	{
		return { Delimiter: tmpSep[1], Text: pText.slice(tmpSep[0].length) };
	}
	/** @type {Record<string, number>} */
	const tmpCounts = { ',': 0, ';': 0, '\t': 0 };
	let tmpQuoted = false;
	for (let i = 0; (i < pText.length) && (i < 65536); i++)
	{
		const tmpChar = pText.charAt(i);
		if (tmpChar === '"')
		{
			tmpQuoted = !tmpQuoted;
		}
		else if (!tmpQuoted && ((tmpChar === '\n') || (tmpChar === '\r')))
		{
			break;
		}
		else if (!tmpQuoted && Object.prototype.hasOwnProperty.call(tmpCounts, tmpChar))
		{
			tmpCounts[tmpChar]++;
		}
	}
	let tmpDelimiter = ',';
	[ ';', '\t' ].forEach((pCandidate) =>
	{
		if (tmpCounts[pCandidate] > tmpCounts[tmpDelimiter])
		{
			tmpDelimiter = pCandidate;
		}
	});
	return { Delimiter: tmpDelimiter, Text: pText };
}

/**
 * CSV / TSV → a table, or fenced text when it has too many rows.
 *
 * Parsed here rather than with SheetJS's string reader: that reader sniffs formats (a CSV whose first line
 * is "TABLE" reads as DIF and comes back empty) and counts blank lines against its row limit.
 *
 * @param {string} pText - the text.
 * @param {boolean} pTabs - tab-separated.
 * @param {Array<string>} pNotes - notes to add to.
 *
 * @return {string|null} markdown.
 */
function _delimitedMarkdown(pText, pTabs, pNotes)
{
	if (!pText.trim())
	{
		return null;
	}
	const tmpSniffed = pTabs ? { Delimiter: '\t', Text: pText } : _sniffDelimiter(pText);
	const tmpParsed = _parseDelimited(tmpSniffed.Text, tmpSniffed.Delimiter, TABLE_ROW_CAP);
	if (tmpParsed.More)
	{
		pNotes.push(`More than ${ TABLE_ROW_CAP.toLocaleString('en-US') } rows: kept as ${ pTabs ? 'TSV' : 'CSV' } text, not a table`);
		return _fence(pText, pTabs ? 'tsv' : 'csv', pNotes);
	}
	if (!tmpParsed.Rows.length || !_rowsHaveText(tmpParsed.Rows))
	{
		return null;
	}
	pNotes.push(`Table: ${ tmpParsed.Rows.length.toLocaleString('en-US') } rows`);
	return _markdownTable(tmpParsed.Rows, pNotes, 'table');
}

/**
 * JSON → a fenced json block, pretty-printed when it parses.
 *
 * @param {string} pText - the text.
 * @param {Array<string>} pNotes - notes to add to.
 *
 * @return {string|null} markdown.
 */
function _jsonMarkdown(pText, pNotes)
{
	if (!pText.trim())
	{
		return null;
	}
	let tmpText = pText;
	try
	{
		tmpText = JSON.stringify(JSON.parse(pText), null, 2);
	}
	catch (pError)
	{
		pNotes.push('Not valid JSON: included as text');
	}
	return _fence(tmpText, 'json', pNotes);
}

/**
 * Sum of the uncompressed sizes a zip's central directory declares (zip-bomb guard), or -1 when the bytes
 * are not a readable zip (e.g. a legacy .xls compound file).
 *
 * @param {Uint8Array} pBytes - the file.
 *
 * @return {number} total bytes, or -1.
 */
function _zipInflatedSize(pBytes)
{
	const tmpLength = pBytes.length;
	/**
	 * @param {number} pAt - offset.
	 * @return {number} little-endian uint16.
	 */
	const fU16 = (pAt) => pBytes[pAt] | (pBytes[pAt + 1] << 8);
	/**
	 * @param {number} pAt - offset.
	 * @return {number} little-endian uint32.
	 */
	const fU32 = (pAt) => (pBytes[pAt] | (pBytes[pAt + 1] << 8) | (pBytes[pAt + 2] << 16) | (pBytes[pAt + 3] << 24)) >>> 0;
	if ((tmpLength < 22) || (pBytes[0] !== 0x50) || (pBytes[1] !== 0x4B))
	{
		return -1;
	}
	let tmpEnd = -1;
	for (let i = tmpLength - 22; i >= Math.max(0, tmpLength - 65557); i--)
	{
		if (fU32(i) === 0x06054B50)
		{
			tmpEnd = i;
			break;
		}
	}
	if (tmpEnd < 0)
	{
		return -1;
	}
	const tmpCount = fU16(tmpEnd + 10);
	let tmpAt = fU32(tmpEnd + 16);
	let tmpTotal = 0;
	for (let n = 0; n < tmpCount; n++)
	{
		if ((tmpAt + 46 > tmpLength) || (fU32(tmpAt) !== 0x02014B50))
		{
			return -1;
		}
		tmpTotal += fU32(tmpAt + 24);
		tmpAt += 46 + fU16(tmpAt + 28) + fU16(tmpAt + 30) + fU16(tmpAt + 32);
	}
	return tmpTotal;
}

/**
 * Throws when a zip declares more than the inflated cap.
 *
 * @param {Uint8Array} pBytes - the file.
 */
function _guardZip(pBytes)
{
	if (_zipInflatedSize(pBytes) > INFLATED_CAP_BYTES)
	{
		throw new Error('Expands to more than 200 MB');
	}
}

/**
 * Workbook → one table per visible sheet.
 *
 * @param {Uint8Array} pBytes - the file.
 * @param {any} pDeps - injected dependencies.
 * @param {Array<string>} pNotes - notes to add to.
 *
 * @return {string|null} markdown.
 */
function _spreadsheetMarkdown(pBytes, pDeps, pNotes)
{
	const tmpXLSX = _resolveXLSX(pDeps);
	if (!tmpXLSX || !tmpXLSX.utils)
	{
		pNotes.push('The spreadsheet reader (SheetJS) is not available');
		return null;
	}
	_guardZip(pBytes);
	const tmpBook = tmpXLSX.read(pBytes, { type: 'array', cellDates: true, cellFormula: false, cellHTML: false, cellStyles: false, bookVBA: false });
	const tmpSheetInfo = (tmpBook.Workbook && Array.isArray(tmpBook.Workbook.Sheets)) ? tmpBook.Workbook.Sheets : [];
	const tmpParts = [];
	let tmpHidden = 0;
	let tmpRowTotal = 0;
	(tmpBook.SheetNames || []).forEach((pName, pIndex) =>
	{
		if (tmpSheetInfo[pIndex] && tmpSheetInfo[pIndex].Hidden)
		{
			tmpHidden++;
			return;
		}
		const tmpWhere = `sheet ${ pIndex + 1 }`;
		const tmpHeading = `## Sheet: ${ String(pName).replace(/[\r\n]+/g, ' ') }\n\n`;
		const tmpSheet = tmpBook.Sheets[pName];
		if (!tmpSheet || !tmpSheet['!ref'])
		{
			tmpParts.push(`${ tmpHeading }_(empty)_\n`);
			return;
		}
		// Clamp the range first so a sheet claiming A1:XFD1048576 is never walked in full.
		const tmpRange = tmpXLSX.utils.decode_range(tmpSheet['!ref']);
		const tmpRowsInRange = tmpRange.e.r - tmpRange.s.r + 1;
		const tmpColumnsInRange = tmpRange.e.c - tmpRange.s.c + 1;
		if (tmpRowsInRange > TABLE_ROW_CAP)
		{
			tmpRange.e.r = tmpRange.s.r + TABLE_ROW_CAP - 1;
			pNotes.push(`${ tmpWhere }: first ${ TABLE_ROW_CAP.toLocaleString('en-US') } of ${ tmpRowsInRange.toLocaleString('en-US') } rows`);
		}
		if (tmpColumnsInRange > TABLE_COLUMN_CAP)
		{
			tmpRange.e.c = tmpRange.s.c + TABLE_COLUMN_CAP - 1;
			pNotes.push(`${ tmpWhere }: first ${ TABLE_COLUMN_CAP } of ${ tmpColumnsInRange } columns`);
		}
		const tmpRows = tmpXLSX.utils.sheet_to_json(tmpSheet, { header: 1, defval: '', raw: false, blankrows: false, range: tmpRange });
		if (!tmpRows.length || !_rowsHaveText(tmpRows))
		{
			tmpParts.push(`${ tmpHeading }_(empty)_\n`);
			return;
		}
		tmpRowTotal += tmpRows.length;
		tmpParts.push(tmpHeading + _markdownTable(tmpRows, pNotes, tmpWhere));
	});
	if (tmpHidden)
	{
		pNotes.push(`${ tmpHidden } hidden sheet${ (tmpHidden === 1) ? '' : 's' } skipped`);
	}
	if (!tmpParts.length)
	{
		return null;
	}
	if (!tmpRowTotal)
	{
		return null;
	}
	pNotes.unshift(`${ tmpParts.length } sheet${ (tmpParts.length === 1) ? '' : 's' }, ${ tmpRowTotal.toLocaleString('en-US') } rows`);
	return tmpParts.join('\n');
}

/**
 * Whether a node is the element namespace:localName.
 *
 * @param {any} pNode - a DOM node.
 * @param {Array<string>} pNamespaces - accepted namespace URIs.
 * @param {string} pLocalName - local name.
 *
 * @return {boolean} true when it is.
 */
function _isElement(pNode, pNamespaces, pLocalName)
{
	return !!pNode && (pNode.nodeType === 1) && (pNode.localName === pLocalName) && (pNamespaces.indexOf(pNode.namespaceURI) >= 0);
}

/**
 * Element children of a node.
 *
 * @param {any} pNode - a DOM node.
 *
 * @return {Array<any>} the child elements.
 */
function _children(pNode)
{
	const tmpChildren = [];
	for (let tmpChild = pNode ? pNode.firstChild : null; tmpChild; tmpChild = tmpChild.nextSibling)
	{
		if (tmpChild.nodeType === 1)
		{
			tmpChildren.push(tmpChild);
		}
	}
	return tmpChildren;
}

/**
 * The first child element namespace:localName.
 *
 * @param {any} pNode - a DOM node.
 * @param {Array<string>} pNamespaces - accepted namespace URIs.
 * @param {string} pLocalName - local name.
 *
 * @return {any} the element or null.
 */
function _child(pNode, pNamespaces, pLocalName)
{
	const tmpChildren = _children(pNode);
	for (let i = 0; i < tmpChildren.length; i++)
	{
		if (_isElement(tmpChildren[i], pNamespaces, pLocalName))
		{
			return tmpChildren[i];
		}
	}
	return null;
}

/**
 * A namespaced attribute (w:val, …), with a prefixed-name fallback.
 *
 * @param {any} pElement - the element.
 * @param {Array<string>} pNamespaces - accepted namespace URIs.
 * @param {string} pPrefix - the conventional prefix.
 * @param {string} pName - local attribute name.
 *
 * @return {string} the value, or ''.
 */
function _attribute(pElement, pNamespaces, pPrefix, pName)
{
	if (!pElement)
	{
		return '';
	}
	for (let i = 0; i < pNamespaces.length; i++)
	{
		const tmpValue = pElement.getAttributeNS(pNamespaces[i], pName);
		if (tmpValue)
		{
			return tmpValue;
		}
	}
	return pElement.getAttribute(`${ pPrefix }:${ pName }`) || pElement.getAttribute(pName) || '';
}

/**
 * Parses XML, throwing when the parser reports an error.
 *
 * @param {any} pDOMParser - DOMParser constructor.
 * @param {string} pXML - the XML text.
 *
 * @return {any} the document.
 */
function _parseXML(pDOMParser, pXML)
{
	const tmpDocument = new pDOMParser().parseFromString(pXML, 'application/xml');
	if (!tmpDocument || !tmpDocument.documentElement || (tmpDocument.getElementsByTagName('parsererror').length > 0))
	{
		throw new Error('The document XML could not be parsed');
	}
	return tmpDocument;
}

/**
 * Text of a zip entry, or null when it is missing.
 *
 * @param {any} pCFB - XLSX.CFB.
 * @param {any} pContainer - the read zip.
 * @param {string} pPath - '/word/document.xml'.
 *
 * @return {string|null} the text.
 */
function _zipEntryText(pCFB, pContainer, pPath)
{
	const tmpEntry = pCFB.find(pContainer, pPath);
	if (!tmpEntry || !tmpEntry.content)
	{
		return null;
	}
	const tmpBytes = _toBytes(tmpEntry.content) || new Uint8Array(tmpEntry.content);
	return _decodeText(tmpBytes);
}

/** @type {Record<string, string>} Word run elements that stand for fixed text. */
const _WordFixedText = { tab: '\t', br: '\n', cr: '\n', noBreakHyphen: '-', softHyphen: '' };
/** Word elements whose text is not document text: deletions, moved-away text, field codes, properties. */
const _WordSkipped = [ 'del', 'moveFrom', 'delText', 'instrText', 'pPr', 'rPr' ];

/**
 * Plain text of a Word paragraph's runs (tabs, breaks; deleted text, field codes and fallback copies skipped).
 *
 * @param {any} pNode - a w:p or a node inside one.
 *
 * @return {string} the text.
 */
function _wordText(pNode)
{
	let tmpText = '';
	_children(pNode).forEach((pChild) =>
	{
		if (pChild.namespaceURI && (NS_MARKUP_COMPATIBILITY.indexOf(pChild.namespaceURI) >= 0) && (pChild.localName === 'Fallback'))
		{
			return;
		}
		if (NS_WORD.indexOf(pChild.namespaceURI) < 0)
		{
			tmpText += _wordText(pChild);
			return;
		}
		const tmpName = pChild.localName;
		if (Object.prototype.hasOwnProperty.call(_WordFixedText, tmpName))
		{
			tmpText += _WordFixedText[tmpName];
		}
		else if (tmpName === 't')
		{
			tmpText += pChild.textContent || '';
		}
		else if (tmpName === 'p')
		{
			// A paragraph inside a text box.
			tmpText += `\n${ _wordText(pChild) }\n`;
		}
		else if (_WordSkipped.indexOf(tmpName) < 0)
		{
			tmpText += _wordText(pChild);
		}
	});
	return tmpText;
}

/**
 * Text of a Word table cell (its paragraphs, nested tables flattened), lines joined with <br>.
 *
 * @param {any} pCell - a w:tc.
 *
 * @return {string} the cell text.
 */
function _wordCellText(pCell)
{
	const tmpParts = [];
	_children(pCell).forEach((pChild) =>
	{
		if (_isElement(pChild, NS_WORD, 'p'))
		{
			tmpParts.push(_wordText(pChild).trim());
		}
		else if (_isElement(pChild, NS_WORD, 'tbl'))
		{
			_children(pChild).forEach((pRow) =>
			{
				if (_isElement(pRow, NS_WORD, 'tr'))
				{
					tmpParts.push(_children(pRow).map((pInner) => _wordCellText(pInner)).filter((pText) => pText).join(' '));
				}
			});
		}
		else if (_isElement(pChild, NS_WORD, 'sdt'))
		{
			tmpParts.push(_wordCellText(_child(pChild, NS_WORD, 'sdtContent')));
		}
	});
	return tmpParts.filter((pText) => pText).join('\n');
}

/**
 * A Word paragraph as markdown: Heading N / Title → '#', numbered or List-styled → '- ', else text.
 *
 * @param {any} pParagraph - a w:p.
 *
 * @return {string} the line, or '' when empty.
 */
function _wordParagraph(pParagraph)
{
	const tmpText = _trimLineEnds(_wordText(pParagraph)).trim();
	if (!tmpText)
	{
		return '';
	}
	const tmpProperties = _child(pParagraph, NS_WORD, 'pPr');
	const tmpStyle = _attribute(_child(tmpProperties, NS_WORD, 'pStyle'), NS_WORD, 'w', 'val');
	let tmpLevel = 0;
	const tmpHeading = /^heading\s*([1-9])$/i.exec(tmpStyle);
	if (tmpHeading)
	{
		tmpLevel = Number(tmpHeading[1]);
	}
	else if (/^title$/i.test(tmpStyle))
	{
		tmpLevel = 1;
	}
	else
	{
		const tmpOutline = _attribute(_child(tmpProperties, NS_WORD, 'outlineLvl'), NS_WORD, 'w', 'val');
		if (/^[0-8]$/.test(tmpOutline))
		{
			tmpLevel = Number(tmpOutline) + 1;
		}
	}
	if (tmpLevel)
	{
		return `${ new Array(Math.min(tmpLevel, 6) + 1).join('#') } ${ _joinLines(tmpText) }`;
	}
	const tmpNumbering = _child(tmpProperties, NS_WORD, 'numPr');
	if (tmpNumbering || /^list/i.test(tmpStyle))
	{
		const tmpDepth = Number(_attribute(_child(tmpNumbering, NS_WORD, 'ilvl'), NS_WORD, 'w', 'val')) || 0;
		return `${ new Array(Math.min(tmpDepth, 8) + 1).join('  ') }- ${ tmpText.replace(/\n/g, ' ') }`;
	}
	return tmpText;
}

/**
 * Word body blocks (paragraphs, tables, content controls) as markdown blocks.
 *
 * @param {any} pParent - w:body or a container.
 * @param {Array<string>} pBlocks - blocks to add to.
 * @param {Array<string>} pNotes - notes to add to.
 * @param {{ Tables: number }} pCounts - running counts.
 */
function _wordBlocks(pParent, pBlocks, pNotes, pCounts)
{
	_children(pParent).forEach((pChild) =>
	{
		if (_isElement(pChild, NS_WORD, 'p'))
		{
			const tmpLine = _wordParagraph(pChild);
			if (tmpLine)
			{
				pBlocks.push(tmpLine);
			}
		}
		else if (_isElement(pChild, NS_WORD, 'tbl'))
		{
			const tmpRows = [];
			_children(pChild).forEach((pRow) =>
			{
				if (_isElement(pRow, NS_WORD, 'tr') && (tmpRows.length < TABLE_ROW_CAP))
				{
					tmpRows.push(_children(pRow).filter((pCell) => _isElement(pCell, NS_WORD, 'tc')).map((pCell) => _wordCellText(pCell)));
				}
			});
			if (tmpRows.length)
			{
				pCounts.Tables++;
				pBlocks.push(_markdownTable(tmpRows, pNotes, `table ${ pCounts.Tables }`));
			}
		}
		else if (_isElement(pChild, NS_WORD, 'sdt'))
		{
			_wordBlocks(_child(pChild, NS_WORD, 'sdtContent'), pBlocks, pNotes, pCounts);
		}
		else if (_isElement(pChild, NS_WORD, 'customXml'))
		{
			_wordBlocks(pChild, pBlocks, pNotes, pCounts);
		}
	});
}

/**
 * Opens an Office zip with XLSX.CFB.
 *
 * @param {Uint8Array} pBytes - the file.
 * @param {any} pDeps - injected dependencies.
 *
 * @return {{ CFB: any, Container: any, DOMParser: any }} the reader parts.
 */
function _openOfficeZip(pBytes, pDeps)
{
	const tmpXLSX = _resolveXLSX(pDeps);
	const tmpCFB = tmpXLSX ? tmpXLSX.CFB : null;
	if (!tmpCFB || (typeof tmpCFB.read !== 'function') || (typeof tmpCFB.find !== 'function'))
	{
		throw new Error('The zip reader (SheetJS CFB) is not available');
	}
	const tmpDOMParser = _resolveDOMParser(pDeps);
	if (!tmpDOMParser)
	{
		throw new Error('The XML reader (DOMParser) is not available');
	}
	_guardZip(pBytes);
	return { CFB: tmpCFB, Container: tmpCFB.read(pBytes, { type: 'array' }), DOMParser: tmpDOMParser };
}

/**
 * Word document → markdown.
 *
 * @param {Uint8Array} pBytes - the file.
 * @param {any} pDeps - injected dependencies.
 * @param {Array<string>} pNotes - notes to add to.
 *
 * @return {string|null} markdown.
 */
function _docxMarkdown(pBytes, pDeps, pNotes)
{
	const tmpZip = _openOfficeZip(pBytes, pDeps);
	let tmpXML = _zipEntryText(tmpZip.CFB, tmpZip.Container, '/word/document.xml');
	if (tmpXML === null)
	{
		// Some writers name the main part differently; the package relationships say where it is.
		const tmpRelationships = _zipEntryText(tmpZip.CFB, tmpZip.Container, '/_rels/.rels');
		if (tmpRelationships !== null)
		{
			const tmpRelDocument = _parseXML(tmpZip.DOMParser, tmpRelationships);
			const tmpList = tmpRelDocument.getElementsByTagName('Relationship');
			for (let i = 0; (i < tmpList.length) && (tmpXML === null); i++)
			{
				const tmpTarget = tmpList[i].getAttribute('Target') || '';
				if (/\/officeDocument$/.test(tmpList[i].getAttribute('Type') || '') && /^[\w./-]+$/.test(tmpTarget))
				{
					tmpXML = _zipEntryText(tmpZip.CFB, tmpZip.Container, `/${ tmpTarget.replace(/^\/+/, '') }`);
				}
			}
		}
	}
	if (tmpXML === null)
	{
		pNotes.push('No document body found');
		return null;
	}
	const tmpDocument = _parseXML(tmpZip.DOMParser, tmpXML);
	const tmpBodies = NS_WORD.map((pNamespace) => tmpDocument.getElementsByTagNameNS(pNamespace, 'body')[0]).filter((pBody) => !!pBody);
	if (!tmpBodies.length)
	{
		pNotes.push('No document body found');
		return null;
	}
	const tmpBlocks = [];
	const tmpCounts = { Tables: 0 };
	_wordBlocks(tmpBodies[0], tmpBlocks, pNotes, tmpCounts);
	if (!tmpBlocks.length)
	{
		return null;
	}
	pNotes.unshift(`${ tmpBlocks.length.toLocaleString('en-US') } block${ (tmpBlocks.length === 1) ? '' : 's' }${ tmpCounts.Tables ? `, ${ tmpCounts.Tables } table${ (tmpCounts.Tables === 1) ? '' : 's' }` : '' }`);
	return `${ tmpBlocks.join('\n\n') }\n`;
}

/**
 * Whether a slide element sits in a title placeholder.
 *
 * @param {any} pNode - a node inside a slide.
 *
 * @return {boolean} true for title text.
 */
function _inSlideTitle(pNode)
{
	for (let tmpNode = pNode; tmpNode; tmpNode = tmpNode.parentNode)
	{
		if (_isElement(tmpNode, NS_PRESENTATION, 'sp'))
		{
			const tmpPlaceholder = _child(_child(_child(tmpNode, NS_PRESENTATION, 'nvSpPr'), NS_PRESENTATION, 'nvPr'), NS_PRESENTATION, 'ph');
			const tmpType = tmpPlaceholder ? (tmpPlaceholder.getAttribute('type') || '') : '';
			return (tmpType === 'title') || (tmpType === 'ctrTitle');
		}
	}
	return false;
}

/**
 * Text of a DrawingML paragraph (a:t runs and fields, a:br breaks).
 *
 * @param {any} pNode - an a:p or a node inside one.
 *
 * @return {string} the text.
 */
function _drawingText(pNode)
{
	let tmpText = '';
	_children(pNode).forEach((pChild) =>
	{
		if (_isElement(pChild, NS_DRAWING, 't'))
		{
			tmpText += pChild.textContent || '';
		}
		else if (_isElement(pChild, NS_DRAWING, 'br'))
		{
			tmpText += '\n';
		}
		else if (!_isElement(pChild, NS_DRAWING, 'pPr') && !_isElement(pChild, NS_DRAWING, 'rPr'))
		{
			tmpText += _drawingText(pChild);
		}
	});
	return tmpText;
}

/**
 * The paragraphs of a slide in document order (fallback copies skipped).
 *
 * @param {any} pNode - the slide root or a node in it.
 * @param {Array<any>} pParagraphs - list to add to.
 */
function _slideParagraphs(pNode, pParagraphs)
{
	_children(pNode).forEach((pChild) =>
	{
		if (_isElement(pChild, NS_DRAWING, 'p'))
		{
			pParagraphs.push(pChild);
		}
		else if (!_isElement(pChild, NS_MARKUP_COMPATIBILITY, 'Fallback'))
		{
			_slideParagraphs(pChild, pParagraphs);
		}
	});
}

/**
 * PowerPoint deck → '## Slide N' sections (slides in file-number order).
 *
 * @param {Uint8Array} pBytes - the file.
 * @param {any} pDeps - injected dependencies.
 * @param {Array<string>} pNotes - notes to add to.
 *
 * @return {string|null} markdown.
 */
function _pptxMarkdown(pBytes, pDeps, pNotes)
{
	const tmpZip = _openOfficeZip(pBytes, pDeps);
	const tmpSlides = [];
	const tmpPaths = tmpZip.Container.FullPaths || [];
	for (let i = 0; i < tmpPaths.length; i++)
	{
		const tmpMatch = /\/ppt\/slides\/slide(\d+)\.xml$/i.exec(tmpPaths[i]);
		if (tmpMatch && tmpZip.Container.FileIndex[i] && tmpZip.Container.FileIndex[i].content)
		{
			tmpSlides.push({ Number: Number(tmpMatch[1]), Entry: tmpZip.Container.FileIndex[i] });
		}
	}
	tmpSlides.sort((pLeft, pRight) => pLeft.Number - pRight.Number);
	if (!tmpSlides.length)
	{
		pNotes.push('No slides found');
		return null;
	}
	let tmpWithText = 0;
	const tmpParts = tmpSlides.map((pSlide, pIndex) =>
	{
		const tmpDocument = _parseXML(tmpZip.DOMParser, _decodeText(_toBytes(pSlide.Entry.content) || new Uint8Array(pSlide.Entry.content)));
		const tmpParagraphs = [];
		_slideParagraphs(tmpDocument.documentElement, tmpParagraphs);
		const tmpLines = [];
		tmpParagraphs.forEach((pParagraph) =>
		{
			const tmpText = _joinLines(_drawingText(pParagraph));
			if (!tmpText)
			{
				return;
			}
			if (_inSlideTitle(pParagraph))
			{
				tmpLines.push(`### ${ tmpText }`);
				return;
			}
			const tmpProperties = _child(pParagraph, NS_DRAWING, 'pPr');
			const tmpDepth = Number(tmpProperties ? tmpProperties.getAttribute('lvl') : 0) || 0;
			tmpLines.push(`${ new Array(Math.min(tmpDepth, 8) + 1).join('  ') }- ${ tmpText }`);
		});
		if (tmpLines.length)
		{
			tmpWithText++;
		}
		return `## Slide ${ pIndex + 1 }\n\n${ tmpLines.length ? tmpLines.join('\n') : '_(no text)_' }\n`;
	});
	if (!tmpWithText)
	{
		pNotes.push('No slide text found');
		return null;
	}
	pNotes.unshift(`${ tmpSlides.length } slide${ (tmpSlides.length === 1) ? '' : 's' }`);
	return tmpParts.join('\n');
}

/**
 * Rejects a promise that takes too long.
 *
 * @param {Promise<any>} pPromise - the work.
 * @param {number} pMilliseconds - the limit.
 * @param {string} pMessage - the timeout error message.
 *
 * @return {Promise<any>} the work, or a timeout rejection.
 */
function _withTimeout(pPromise, pMilliseconds, pMessage)
{
	return new Promise((fResolve, fReject) =>
	{
		const tmpTimer = setTimeout(() => fReject(new Error(pMessage)), pMilliseconds);
		pPromise.then(
			(pValue) =>
			{
				clearTimeout(tmpTimer);
				fResolve(pValue);
			},
			(pError) =>
			{
				clearTimeout(tmpTimer);
				fReject(pError);
			});
	});
}

/**
 * Loads pdf.js on first use: a `<script type="module">` for js/pdfjs-dist/build/pdf.mjs (which sets
 * `globalThis.pdfjsLib`), then points its worker at pdf.worker.mjs. Cached; a failed load can be retried.
 *
 * @return {Promise<any>} pdfjsLib.
 */
function defaultLoadPdfJs()
{
	if (_PdfJsPromise)
	{
		return _PdfJsPromise;
	}
	const tmpLoad = new Promise((fResolve, fReject) =>
	{
		const tmpGlobal = _global();
		if (tmpGlobal.pdfjsLib && (typeof tmpGlobal.pdfjsLib.getDocument === 'function'))
		{
			fResolve(tmpGlobal.pdfjsLib);
			return;
		}
		if ((typeof document === 'undefined') || !document.createElement || !document.head)
		{
			fReject(new Error('pdf.js needs a browser page'));
			return;
		}
		const tmpScript = document.createElement('script');
		tmpScript.type = 'module';
		tmpScript.src = PDFJS_SCRIPT_SRC;
		tmpScript.onload = () =>
		{
			// A module has run by its load event; poll briefly in case the global lands a tick later.
			let tmpTries = 0;
			const fCheck = () =>
			{
				const tmpLib = _global().pdfjsLib;
				if (tmpLib && (typeof tmpLib.getDocument === 'function'))
				{
					fResolve(tmpLib);
				}
				else if (++tmpTries > 40)
				{
					fReject(new Error('pdf.js did not initialise'));
				}
				else
				{
					setTimeout(fCheck, 50);
				}
			};
			fCheck();
		};
		tmpScript.onerror = () => fReject(new Error('pdf.js failed to load'));
		document.head.appendChild(tmpScript);
	});
	_PdfJsPromise = _withTimeout(tmpLoad, PDFJS_LOAD_TIMEOUT_MS, 'pdf.js took too long to load').then((pLib) =>
	{
		if (pLib.GlobalWorkerOptions)
		{
			pLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_SRC;
		}
		return pLib;
	});
	_PdfJsPromise.catch(() =>
	{
		_PdfJsPromise = null;
	});
	return _PdfJsPromise;
}

/**
 * Text of one pdf.js getTextContent() result.
 *
 * @param {any} pContent - { items: [{ str, hasEOL }] }.
 *
 * @return {string} the page text.
 */
function _pdfPageText(pContent)
{
	let tmpText = '';
	((pContent && pContent.items) || []).forEach((pItem) =>
	{
		if (pItem && (typeof pItem.str === 'string'))
		{
			tmpText += pItem.str;
			if (pItem.hasEOL)
			{
				tmpText += '\n';
			}
		}
	});
	return _trimLineEnds(tmpText).replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * PDF → '## Page N' sections; null with "looks scanned" when there is too little text.
 *
 * @param {Uint8Array} pBytes - the file.
 * @param {any} pDeps - injected dependencies.
 * @param {Array<string>} pNotes - notes to add to.
 *
 * @return {Promise<string|null>} markdown.
 */
function _pdfMarkdown(pBytes, pDeps, pNotes)
{
	const fLoad = (pDeps && (typeof pDeps.loadPdfJs === 'function')) ? pDeps.loadPdfJs : defaultLoadPdfJs;
	/** @type {any} */
	let tmpTask = null;
	const tmpWork = Promise.resolve()
		.then(() => fLoad())
		.then((pLib) =>
		{
			if (!pLib || (typeof pLib.getDocument !== 'function'))
			{
				throw new Error('pdf.js is not available');
			}
			// pdf.js hands the buffer to its worker, so give it a copy; never evaluate font programs.
			tmpTask = pLib.getDocument({ data: pBytes.slice(0), isEvalSupported: false, disableFontFace: true });
			return tmpTask.promise;
		})
		.then((pDocument) =>
		{
			const tmpPageCount = Number(pDocument.numPages) || 0;
			const tmpPages = [];
			let tmpLength = 0;
			let tmpCharacters = 0;
			let tmpPage = 1;
			/**
			 * Reads pages one at a time until the last page, the page cap or the output cap.
			 * @return {Promise<void>} done.
			 */
			const fNext = () =>
			{
				if ((tmpPage > tmpPageCount) || (tmpPage > PDF_PAGE_CAP) || (tmpLength > OUTPUT_CAP_CHARS))
				{
					return Promise.resolve();
				}
				return Promise.resolve(pDocument.getPage(tmpPage))
					.then((pPage) => Promise.resolve(pPage.getTextContent()).then((pContent) =>
					{
						const tmpText = _pdfPageText(pContent);
						const tmpSection = `## Page ${ tmpPage }\n\n${ tmpText }\n`;
						tmpPages.push(tmpSection);
						tmpLength += tmpSection.length;
						tmpCharacters += tmpText.replace(/\s+/g, '').length;
						if (typeof pPage.cleanup === 'function')
						{
							pPage.cleanup();
						}
						tmpPage++;
						return fNext();
					}));
			};
			return fNext().then(() =>
			{
				const tmpRead = tmpPages.length;
				if (!tmpRead || ((tmpCharacters / tmpRead) < SCANNED_CHARS_PER_PAGE))
				{
					pNotes.push('PDF looks scanned (little or no text): no text extracted');
					return null;
				}
				if (tmpRead < tmpPageCount)
				{
					pNotes.push(`First ${ tmpRead.toLocaleString('en-US') } of ${ tmpPageCount.toLocaleString('en-US') } pages`);
				}
				pNotes.unshift(`${ tmpPageCount.toLocaleString('en-US') } page${ (tmpPageCount === 1) ? '' : 's' }`);
				return tmpPages.join('\n');
			});
		});
	/** Frees the pdf.js document and worker; failures here never matter. */
	const fRelease = () =>
	{
		try
		{
			if (tmpTask && (typeof tmpTask.destroy === 'function'))
			{
				Promise.resolve(tmpTask.destroy()).catch(() => null);
			}
		}
		catch (pError)
		{
			// Ignored.
		}
	};
	return _withTimeout(tmpWork, PDF_CONVERT_TIMEOUT_MS, 'The PDF took too long to read').then(
		(pMarkdown) =>
		{
			fRelease();
			return pMarkdown;
		},
		(pError) =>
		{
			fRelease();
			throw pError;
		});
}

/**
 * The conversion proper (may reject; convertFileToMarkdown catches).
 *
 * @param {any} pFile - a File-like object.
 * @param {{ Kind: string, TextNative: boolean }} pClass - its classification.
 * @param {any} pDeps - injected dependencies.
 *
 * @return {Promise<{ Markdown: string|null, Kind: string, Notes: Array<string> }>} the result.
 */
function _convert(pFile, pClass, pDeps)
{
	const tmpKind = pClass.Kind;
	if (tmpKind === 'binary')
	{
		return Promise.resolve(_noText(tmpKind, 'No text conversion for this file type'));
	}
	if ((typeof pFile.size === 'number') && (pFile.size > INPUT_CAP_BYTES))
	{
		return Promise.resolve(_noText(tmpKind, 'Too large to convert (over 25 MB)'));
	}
	/** @type {Array<string>} */
	const tmpNotes = [];
	return _readBytes(pFile)
		.then((pBytes) =>
		{
			if (!pBytes && (typeof pFile.text === 'function') && (_BinaryKinds.indexOf(tmpKind) < 0))
			{
				// A File-like object that only offers text().
				return Promise.resolve(pFile.text()).then((pText) => ({ Bytes: null, Text: String(pText) }));
			}
			return { Bytes: pBytes, Text: null };
		})
		.then((pRead) =>
		{
			if (!pRead.Bytes && (pRead.Text === null))
			{
				return _noText(tmpKind, 'The file could not be read');
			}
			const tmpSize = pRead.Bytes ? pRead.Bytes.length : pRead.Text.length;
			if (tmpSize > INPUT_CAP_BYTES)
			{
				return _noText(tmpKind, 'Too large to convert (over 25 MB)');
			}
			if (_BinaryKinds.indexOf(tmpKind) >= 0)
			{
				return Promise.resolve(_binaryMarkdown(tmpKind, pRead.Bytes, pDeps, tmpNotes)).then((pMarkdown) => _result(pMarkdown, tmpKind, tmpNotes));
			}
			const tmpText = (pRead.Text !== null) ? pRead.Text.replace(/^\uFEFF/, '') : _decodeText(pRead.Bytes);
			if (tmpText.slice(0, 8192).indexOf('\u0000') >= 0)
			{
				return _noText(tmpKind, 'Looks like binary data, not text');
			}
			const tmpExtension = _extensionOf(pFile.name);
			const tmpType = ((typeof pFile.type === 'string') ? pFile.type : '').toLowerCase();
			let tmpMarkdown = tmpText;
			if (tmpKind === 'csv')
			{
				tmpMarkdown = _delimitedMarkdown(tmpText, (tmpExtension === 'tsv') || (tmpType.indexOf('text/tab-separated-values') === 0), tmpNotes);
			}
			else if (tmpKind === 'json')
			{
				tmpMarkdown = _jsonMarkdown(tmpText, tmpNotes);
			}
			else if (tmpKind === 'markup')
			{
				tmpMarkdown = tmpText.trim() ? _fence(tmpText, ((tmpExtension === 'xml') || (tmpType.indexOf('xml') >= 0)) ? 'xml' : 'html', tmpNotes) : null;
			}
			return _result(tmpMarkdown, tmpKind, tmpNotes);
		});
}

/**
 * Dispatches the binary kinds.
 *
 * @param {string} pKind - spreadsheet / docx / pptx / pdf.
 * @param {Uint8Array} pBytes - the file.
 * @param {any} pDeps - injected dependencies.
 * @param {Array<string>} pNotes - notes to add to.
 *
 * @return {string|null|Promise<string|null>} markdown.
 */
function _binaryMarkdown(pKind, pBytes, pDeps, pNotes)
{
	if (pKind === 'spreadsheet')
	{
		return _spreadsheetMarkdown(pBytes, pDeps, pNotes);
	}
	if (pKind === 'docx')
	{
		return _docxMarkdown(pBytes, pDeps, pNotes);
	}
	if (pKind === 'pptx')
	{
		return _pptxMarkdown(pBytes, pDeps, pNotes);
	}
	if (pKind === 'pdf')
	{
		return _pdfMarkdown(pBytes, pDeps, pNotes);
	}
	pNotes.push('No text conversion for this file type');
	return null;
}

/**
 * The final result: empty text counts as no text; long text is capped.
 *
 * @param {string|null} pMarkdown - converted markdown.
 * @param {string} pKind - the file kind.
 * @param {Array<string>} pNotes - notes so far.
 *
 * @return {{ Markdown: string|null, Kind: string, Notes: Array<string> }} the result.
 */
function _result(pMarkdown, pKind, pNotes)
{
	if ((typeof pMarkdown !== 'string') || !pMarkdown.trim())
	{
		if (!pNotes.length)
		{
			pNotes.push('No text found');
		}
		return { Markdown: null, Kind: pKind, Notes: pNotes };
	}
	return { Markdown: _capOutput(pMarkdown, pNotes), Kind: pKind, Notes: pNotes };
}

/**
 * Converts a dropped file to markdown for the corpus. Never rejects.
 *
 * @param {any} pFile - a File (or { name, type, size, arrayBuffer?(), text?() }).
 * @param {{ XLSX?: any, DOMParser?: any, loadPdfJs?: function(): Promise<any> }} [pDeps] - injected dependencies.
 *
 * @return {Promise<{ Markdown: string|null, Kind: string, Notes: Array<string> }>} the result.
 */
function convertFileToMarkdown(pFile, pDeps)
{
	let tmpKind = 'binary';
	return new Promise((fResolve) =>
	{
		try
		{
			if (!pFile || (typeof pFile !== 'object'))
			{
				fResolve(_noText(tmpKind, 'No file'));
				return;
			}
			const tmpClass = classifyFile(pFile.name, pFile.type);
			tmpKind = tmpClass.Kind;
			_convert(pFile, tmpClass, pDeps || {}).then(
				fResolve,
				(pError) => fResolve(_noText(tmpKind, `Could not convert: ${ _errorText(pError) }`)));
		}
		catch (pError)
		{
			fResolve(_noText(tmpKind, `Could not convert: ${ _errorText(pError) }`));
		}
	});
}

module.exports =
{
	convertFileToMarkdown: convertFileToMarkdown,
	classifyFile: classifyFile,
	defaultLoadPdfJs: defaultLoadPdfJs,
};
